import React, { useMemo } from "react";
import pluralize from "pluralize";
import PropTypes from "prop-types";

import PhoneNumberToggle from "src/shared/contact-boxes/components/PhoneNumberToggle";
import contactProps from "src/shared/utilities/graphql/contactProps";

import * as S from "./styled";

const ContactInfo = ({
  buildingId,
  className,
  contact,
  dealType,
  expertsApiUuid,
  hidePhone,
  isMobile,
  isNewAgentProfileEnabled,
  listingId,
  onInfoClick,
  onPhoneNumberClick,
  recentDealsCount,
  recentDealsCountPostfix,
  searchId,
  segment,
  showAdvertisementDisclosure
}) => {
  const { business_name, id, license, name, phone, profile } = contact;

  const hasRecentDeals = Boolean(recentDealsCount);
  const handleButtonClick = () => (hasRecentDeals ? onInfoClick?.(id) : null);
  const handleClickShowPhoneNumber = () => onPhoneNumberClick?.(false);
  const handleClickMobilePhoneIcon = () => onPhoneNumberClick?.(true);

  const infoButtonType = hasRecentDeals && onInfoClick ? "button" : "span";

  const constructProfileUrl = () => {
    const possibleParams = [
      { searchId },
      { buildingId },
      { listingId },
      { expertsApiUuid },
      { segment }
    ].filter(param => Object.values(param).every(val => !(val == null)));

    const queryString = new URLSearchParams(
      Object.assign({}, ...possibleParams)
    ).toString();

    return profile?.url && queryString
      ? `${profile?.url}?${queryString}`
      : profile?.url;
  };

  const hrefUrl = useMemo(() => constructProfileUrl(), [
    searchId,
    buildingId,
    listingId,
    expertsApiUuid,
    segment,
    isNewAgentProfileEnabled
  ]);

  return (
    <S.InfoWrapper className={className}>
      <S.ContactAvatar contact={contact} />
      <S.InfoContainer hidePhone={hidePhone}>
        {isNewAgentProfileEnabled && profile?.url ? (
          <S.InfoButton
            as="a"
            data-testid="contact-name"
            href={hrefUrl}
            rel="noopener noreferrer"
            target="_blank"
          >
            {name}
          </S.InfoButton>
        ) : (
          <S.InfoButton
            as={infoButtonType}
            data-testid="contact-name"
            onClick={handleButtonClick}
            type={infoButtonType}
          >
            {name}
          </S.InfoButton>
        )}
        {license?.display_type && (
          <S.Text data-testid="contact-license-type">
            {license.display_type}
          </S.Text>
        )}
        <S.Text>{business_name}</S.Text>
        {hasRecentDeals ? (
          <S.Text>
            <S.InfoDealsButton
              data-testid="recent-deals"
              onClick={handleButtonClick}
              type="button"
            >
              {pluralize(`${dealType} deal`, recentDealsCount, true)}
            </S.InfoDealsButton>

            {recentDealsCountPostfix && (
              <S.DesktopOnly> {recentDealsCountPostfix}</S.DesktopOnly>
            )}
          </S.Text>
        ) : null}
        {!hidePhone && (
          <PhoneNumberToggle onClick={handleClickShowPhoneNumber}>
            {phone}
          </PhoneNumberToggle>
        )}
        {showAdvertisementDisclosure && (
          <S.AdvertisementDisclosure>Advertisement</S.AdvertisementDisclosure>
        )}
      </S.InfoContainer>
      {isMobile && !hidePhone && (
        <S.PhoneLink href={`tel:${phone}`} onClick={handleClickMobilePhoneIcon}>
          <S.PhoneIcon />
        </S.PhoneLink>
      )}
    </S.InfoWrapper>
  );
};

ContactInfo.propTypes = {
  buildingId: PropTypes.number,
  className: PropTypes.string,
  contact: PropTypes.shape(contactProps).isRequired,
  dealType: PropTypes.string,
  expertsApiUuid: PropTypes.string,
  hidePhone: PropTypes.bool,
  isMobile: PropTypes.bool,
  isNewAgentProfileEnabled: PropTypes.bool,
  listingId: PropTypes.number,
  onInfoClick: PropTypes.func,
  onPhoneNumberClick: PropTypes.func,
  recentDealsCount: PropTypes.number,
  recentDealsCountPostfix: PropTypes.string,
  searchId: PropTypes.number,
  segment: PropTypes.string,
  showAdvertisementDisclosure: PropTypes.bool
};

ContactInfo.defaultProps = {
  buildingId: null,
  className: "",
  dealType: "recent",
  expertsApiUuid: null,
  hidePhone: false,
  isMobile: false,
  isNewAgentProfileEnabled: false,
  listingId: null,
  onInfoClick: null,
  onPhoneNumberClick: () => null,
  recentDealsCount: null,
  recentDealsCountPostfix: null,
  searchId: null,
  segment: null,
  showAdvertisementDisclosure: false
};

export default ContactInfo;
